import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import { ConvertedToObjectType } from 'types/Locale';

/**
 * Post-processor to highlight translations in non "prod" environments.
 */
const highlightPostProcessor = {
  name: 'highlightTranslations',
  type: 'postProcessor' as const,
  process(value: string, _key: string, _options: any, _translator: any) {
    // Check for URL parameter to enable highlighting
    const urlParams = new URLSearchParams(window.location.search);
    const highlightEnabled = urlParams.get('i18n-highlight') === 'true';

    if (highlightEnabled) {
      // highlight using visible Unicode characters as any injected HTML is
      // safely escaped
      return `🔴${value}🔴`;
    }
    return value;
  },
};

import cs from './cs/translation.json';
import da from './da/translation.json';
import de from './de/translation.json';
import en from './en/translation.json';
import es from './es/translation.json';
import fi from './fi/translation.json';
import fr from './fr/translation.json';
import frCA from './fr-CA/translation.json';
import hi from './hi/translation.json';
import id from './id/translation.json';
import it from './it/translation.json';
import ja from './ja/translation.json';
import ko from './ko/translation.json';
import nl from './nl/translation.json';
import pl from './pl/translation.json';
import pt from './pt/translation.json';
import ru from './ru/translation.json';
import sv from './sv/translation.json';
import vi from './vi/translation.json';
import zh from './zh/translation.json';
import zhTW from './zh-TW/translation.json';

// Add json translations here
const translationsJson = {
  cs: { translation: cs },
  da: { translation: da },
  de: { translation: de },
  en: { translation: en },
  es: { translation: es },
  fi: { translation: fi },
  fr: { translation: fr },
  frCA: { translation: frCA },
  hi: { translation: hi },
  id: { translation: id },
  it: { translation: it },
  ja: { translation: ja },
  ko: { translation: ko },
  nl: { translation: nl },
  pl: { translation: pl },
  pt: { translation: pt },
  ru: { translation: ru },
  sv: { translation: sv },
  vi: { translation: vi },
  zh: { translation: zh },
  zhTW: { translation: zhTW },
};

type LanguageCodesType = {
  [key: string]: string;
};

export const languageCodes: LanguageCodesType = {
  'Chinese (Traditional)': 'zh-TW',
  'French (Canada)': 'fr-CA',
  Czech: 'cs',
  Danish: 'da',
  Dutch: 'nl',
  English: 'en',
  French: 'fr',
  German: 'de',
  Hindi: 'hi',
  Indonesian: 'id',
  Italian: 'it',
  Japanese: 'ja',
  Korean: 'ko',
  Mandarin: 'zh',
  Polish: 'pl',
  Portuguese: 'pt',
  Russian: 'ru',
  Spanish: 'es',
  Swedish: 'sv',
};

export type TranslationResource = typeof en;
export type LanguageKey = keyof TranslationResource;

export const translations: ConvertedToObjectType<TranslationResource> =
  {} as any;

/*
 * Converts the static JSON file into an object where keys are identical
 * but values are strings concatenated according to syntax.
 * This is helpful when using the JSON file keys and still have the intellisense support
 * along with type-safety
 */
const convertLanguageJsonToObject = (obj: any, dict: any, current?: string) => {
  Object.keys(obj).forEach((key) => {
    const currentLookupKey = current ? `${current}.${key}` : key;
    if (typeof obj[key] === 'object') {
      dict[key] = {};
      convertLanguageJsonToObject(obj[key], dict[key], currentLookupKey);
    } else {
      dict[key] = currentLookupKey;
    }
  });
};

export const i18n = (language: string): any =>
  i18next
    // Add the highlight post-processor
    .use(highlightPostProcessor)
    // pass the i18n instance to react-i18next.
    .use(initReactI18next)
    // detect user language
    // learn more: https://github.com/i18next/i18next-browser-languageDetector
    .use(LanguageDetector)
    // init i18next
    // for all options read: https://www.i18next.com/overview/configuration-options
    .init(
      {
        resources: translationsJson,
        fallbackLng: language,
        // debug: process.env.NODE_ENV !== 'production',
        debug: false,
        react: {
          useSuspense: true,
        },
        interpolation: {
          // React already escapes text nodes against XSS; letting i18next
          // escape again double-encodes characters like `'` into `&#39;`,
          // which then render as literal entities (see IPS-678).
          escapeValue: false,
        },
        // Enable post-processing for highlighting in Dev and Staging
        postProcess:
          process.env.REACT_APP_ENV &&
          ['dev', 'staging'].includes(process.env.REACT_APP_ENV)
            ? ['highlightTranslations']
            : [],
      },
      () => {
        convertLanguageJsonToObject(en, translations);
      },
    );
